import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import pageStyles from './BranchDetail.module.css';
import styles from './Home.module.css';
import Ico from '../components/common/Ico';
import EmptyState from '../components/common/EmptyState';
import ActiveBranchCard from '../components/home/ActiveBranchCard';
import HomeReleasesPanel from '../components/home/HomeReleasesPanel';
import NeedsAttentionPanel from '../components/home/NeedsAttentionPanel';
import { apiGetBranchesList } from '../api/branches';
import { apiGetReleasesList } from '../api/releases';
import { apiListNightlyRuns } from '../api/nightly';
import { WebSocketContext } from '../api/socket';

const ICO_BRANCH = 'M6 3v12 M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M18 9a9 9 0 0 1-9 9';
const ICO_MOON   = 'M12 3a9 9 0 1 0 9 9c0-.46-.04-.92-.1-1.36A5.39 5.39 0 0 1 12.5 3.1 9.06 9.06 0 0 0 12 3z';

const EMPTY_STATE = {
  branches: { loaded: false, list: [] },
  topReleases: { loaded: false, list: [] },
  branchReleases: { loaded: false, byVersion: {} },
  extras: { loaded: false, byVersion: {} },
};

export default function Home({ user }) {
  const ws = useContext(WebSocketContext);
  const [branches, setBranches] = useState(EMPTY_STATE.branches);
  const [topReleases, setTopReleases] = useState(EMPTY_STATE.topReleases);
  const [branchReleases, setBranchReleases] = useState(EMPTY_STATE.branchReleases);
  const [extras, setExtras] = useState(EMPTY_STATE.extras);

  const reloadData = () => {
    Promise.all([
      apiGetBranchesList({ status: 'active' }),
      apiGetReleasesList({ status: 'released', limit: 5 }),
    ]).then(([branchesRes, topReleasesRes]) => {
      const active = [...branchesRes.branches].sort((a, b) => b.version.localeCompare(a.version, undefined, { numeric: true }));
      const visible = active.slice(0, 3);
      setBranches({ loaded: true, list: active });
      setTopReleases({ loaded: true, list: topReleasesRes.releases });

      const branchReleasesPromise = visible.length > 0
        ? apiGetReleasesList({ status: 'released', branch_version: visible.map((b) => b.version).join(',') })
        : Promise.resolve({ releases: [] });

      Promise.all([
        branchReleasesPromise,
        ...active.map((b) =>
          apiListNightlyRuns(b.version, { limit: 14, include_deployments: false })
            .then((nightlyRes) => [b.version, nightlyRes.nightly_runs || []])
        ),
      ]).then(([branchReleasesRes, ...nightlyPairs]) => {
        const byVersion = {};
        branchReleasesRes.releases.forEach((r) => {
          if (!byVersion[r.branch_version]) byVersion[r.branch_version] = r;
        });
        setBranchReleases({ loaded: true, byVersion });
        setExtras({ loaded: true, byVersion: Object.fromEntries(nightlyPairs) });
      });
    }).catch(() => {
      setBranches({ loaded: true, list: [] });
      setTopReleases({ loaded: true, list: [] });
      setBranchReleases({ loaded: true, byVersion: {} });
      setExtras({ loaded: true, byVersion: {} });
    });
  };

  useEffect(() => {
    reloadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!ws) return;
    ws.on('branch_update', reloadData);
    ws.on('release_update', reloadData);
    return () => { ws.off('branch_update'); ws.off('release_update'); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws]);

  const loaded = branches.loaded && topReleases.loaded && branchReleases.loaded && extras.loaded;

  if (!loaded) {
    return (
      <div className={pageStyles.loading}>
        <div className={pageStyles.loadingSpinner} />
        Loading overview…
      </div>
    );
  }

  const activeBranches = branches.list;
  const visibleBranches = activeBranches.slice(0, 3);
  const latestRelease = topReleases.list.find((r) => r.is_latest) || topReleases.list[0];

  const nightlyHealthy = activeBranches.every((b) => {
    const runs = extras.byVersion[b.version] || [];
    const newest = runs[0];
    return !newest || newest.status !== 'failed';
  });

  const attentionItems = [];
  activeBranches.forEach((b) => {
    const runs = extras.byVersion[b.version] || [];
    const newest = runs[0];
    if (newest?.status === 'failed') {
      attentionItems.push({
        icon: ICO_MOON,
        iconColor: 'var(--error-600)',
        text: `${b.version} nightly failing`,
        to: `/branches/${b.version}`,
      });
    }
  });

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.inner}>
        <div className={styles.header}>
          <div className={styles.title}>Overview</div>
          <div className={styles.subtitle}>
            {activeBranches.length} active branch{activeBranches.length === 1 ? '' : 'es'} · nightly {nightlyHealthy ? 'healthy' : 'issues'}
            {latestRelease && ` · ${latestRelease.version} latest release`}
          </div>
        </div>

        <div className={styles.grid}>
          <div className={styles.leftCol}>
            <div className={styles.sectionLabel}>
              <Ico d={ICO_BRANCH} size={12} strokeWidth={1.8} />
              Active branches
            </div>
            {activeBranches.length === 0 ? (
              <EmptyState icon={ICO_BRANCH} title="No active branches" hint="Branches appear here once cut and active." />
            ) : (
              visibleBranches.map((b) => (
                <ActiveBranchCard
                  key={b.version}
                  branch={b}
                  latestRelease={branchReleases.byVersion[b.version]}
                  nightlyRuns={extras.byVersion[b.version]}
                />
              ))
            )}
            {activeBranches.length > 0 && (
              <Link to="/branches" className={styles.viewAllLink}>View all branches</Link>
            )}
            <div className={styles.newBranchHint}>
              New branches appear here when cut — this column stays a live status board rather than a long list.
            </div>
          </div>

          <div className={styles.rightCol}>
            <HomeReleasesPanel releases={topReleases.list} />
            <NeedsAttentionPanel items={attentionItems} />
          </div>
        </div>
      </div>
    </div>
  );
}
